/**
 * R8 Coverage tab — media coverage feeds, distinct from surveillance.
 *
 * Honesty rail (CD R8): a throttled media feed degrades the
 * under-reported signal, not case counts. The "asymmetry join" mark
 * shows which feed powers the §7.20 coverage-gap lever.
 */

import { StateBadge } from './primitives';
import { SAMPLE_COVERAGE_FEEDS } from './sample-data';

export function CoverageView() {
  return (
    <div>
      <section style={{
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
        overflow: 'hidden',
      }}>
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px',
          borderBottom: '1px solid var(--bm-border)',
        }}>
          <span style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 700,
            letterSpacing: '.4px', color: 'var(--bm-text)',
          }}>Media coverage feeds</span>
          <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)' }}>
            distinct from surveillance — feeds §7.20 + the coverage-asymmetry signal
          </span>
        </header>
        {SAMPLE_COVERAGE_FEEDS.map(f => {
          const stateColor = f.state === 'throttled'
            ? 'var(--bm-q-throttled)'
            : f.state === 'live' ? 'var(--bm-state-live)' : 'var(--bm-state-idle)';
          return (
            <div key={f.id} style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.4fr) auto minmax(0, 1.6fr) auto',
              gap: 12, alignItems: 'center',
              padding: '9px 12px',
              borderLeft: `3px solid ${stateColor}`,
              borderBottom: '1px solid var(--bm-border)',
              fontSize: 12, lineHeight: 1.3,
            }}>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontWeight: 600, color: 'var(--bm-text)' }}>{f.name}</span>
                <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10, color: 'var(--bm-text-subtle)' }}>
                  {f.tier !== 'n/a' ? `${f.tier} tier` : 'media'}
                </span>
              </span>
              {f.state === 'throttled' ? (
                <span style={{
                  fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
                  color: 'var(--bm-q-throttled)',
                  background: 'var(--bm-prov-stale-bg)',
                  border: '1px solid var(--bm-state-stale)',
                  borderRadius: 5, padding: '1px 6px',
                }}>⏳ THROTTLED</span>
              ) : (
                <StateBadge state={f.state} small />
              )}
              <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)' }}>
                {f.got}/{f.want} windows · {f.articles.toLocaleString()} articles
                {f.note && <span style={{ color: 'var(--bm-text-subtle)' }}> · {f.note}</span>}
              </span>
              {f.asymmetryFeeds ? (
                <span
                  title="feeds the coverage-asymmetry field used by §7.20 + Phase-6 alert rules"
                  style={{
                    fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
                    color: 'var(--bm-cov-under, #b06)',
                    border: '1px solid var(--bm-cov-under, #b06)',
                    borderRadius: 4, padding: '1px 6px',
                  }}
                >◍ asymmetry join</span>
              ) : <span />}
            </div>
          );
        })}
      </section>

      <div style={{
        marginTop: 10, padding: '10px 14px',
        fontSize: 12, color: 'var(--bm-text-subtle)',
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-sm)',
      }}>
        Coverage feeds are <b>separate from surveillance</b> — a throttled media feed degrades the under-reported signal, not case counts.
        The <b style={{ color: 'var(--bm-cov-under, #b06)' }}>◍ asymmetry join</b> marks which feed powers the Phase-5/6 coverage-gap lever.
      </div>
    </div>
  );
}
